import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { RecordingView, TranscriptionJobView } from '../../../shared/api.js';
import { api } from '../lib/api';
import { formatDateTime, formatShortDateTime } from '../lib/format';

function length(sec: number | undefined | null): string | undefined {
  if (sec === undefined || sec === null) return undefined;
  const minutes = Math.round(sec / 60);
  return minutes < 60 ? `${minutes} мин` : `${Math.floor(minutes / 60)} ч ${minutes % 60} мин`;
}

function size(bytes: number | undefined): string | undefined {
  return bytes === undefined ? undefined : `${Math.max(1, Math.round(bytes / 1_048_576))} МБ`;
}

const JOB_LABEL: Record<TranscriptionJobView['status'], { text: string; className: string }> = {
  queued: { text: 'в очереди', className: 'pill pill--warn' },
  running: { text: 'расшифровывается…', className: 'pill pill--warn' },
  done: { text: 'расшифровано', className: 'pill pill--ok' },
  failed: { text: 'ошибка', className: 'pill pill--error' },
};

function JobState({ job, onOpen }: { job: TranscriptionJobView; onOpen: (id: number) => void }) {
  const label = JOB_LABEL[job.status];
  return (
    <span className="recording__job">
      <span className={label.className}>{label.text}</span>
      {job.status === 'done' && job.transcriptId !== null && (
        <button
          type="button"
          className="btn btn--small btn--ghost"
          onClick={() => onOpen(job.transcriptId!)}
        >
          Открыть
        </button>
      )}
      {job.status === 'failed' && job.error && (
        <span className="text-error small" title={job.error}>
          {job.attempts >= 3 ? 'не удалось' : 'повторим'}: {job.error.slice(0, 120)}
        </span>
      )}
    </span>
  );
}

function RecordingRow({
  recording,
  canWrite,
  engines,
  onOpen,
}: {
  recording: RecordingView;
  canWrite: boolean;
  engines: string[];
  onOpen: (id: number) => void;
}) {
  const queryClient = useQueryClient();
  const transcribe = useMutation({
    mutationFn: (engine: 'elevenlabs' | 'zoom') =>
      api.transcribeRecording(recording.id, { engine }),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['recordings'] }),
        queryClient.invalidateQueries({ queryKey: ['transcription-jobs'] }),
      ]),
  });
  const audio = recording.files.find((f) => f.kind === 'audio' || f.kind === 'video');
  const meta = [
    formatDateTime(recording.start),
    length(recording.durationSec),
    audio ? `${audio.type}${size(audio.bytes) ? ` ${size(audio.bytes)}` : ''}` : 'без аудио',
    recording.hasSourceTranscript ? 'есть транскрипт Zoom' : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
  const job = recording.job;
  const busy = job?.status === 'queued' || job?.status === 'running';
  const canElevenlabs = engines.includes('elevenlabs') && recording.transcribable;
  const canZoom = engines.includes('zoom') && recording.hasSourceTranscript;
  return (
    <li className="page-row">
      <div className="page-row__main">
        <span className="page-row__title">{recording.topic || 'Без темы'}</span>
        <span className="muted small">{meta}</span>
        {transcribe.isError && <span className="text-error small">{transcribe.error.message}</span>}
      </div>
      <div className="actions-row">
        {job && <JobState job={job} onOpen={onOpen} />}
        {canWrite && !busy && job?.status !== 'done' && canElevenlabs && (
          <button
            type="button"
            className="btn btn--small btn--primary"
            disabled={transcribe.isPending}
            onClick={() => transcribe.mutate('elevenlabs')}
          >
            {job?.status === 'failed' ? 'Повторить' : 'Расшифровать'}
          </button>
        )}
        {canWrite && !busy && job?.status !== 'done' && canZoom && (
          <button
            type="button"
            className="btn btn--small"
            disabled={transcribe.isPending}
            title="Взять транскрипт, который сделал сам Zoom"
            onClick={() => transcribe.mutate('zoom')}
          >
            Транскрипт Zoom
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * Zoom cloud recordings of the last 30 days with the state of their transcription. Queuing one
 * needs the right to write transcripts; the queue runs on the server and the list refreshes
 * itself while a job is going.
 */
export function RecordingsPanel({ onOpen }: { onOpen: (transcriptId: number) => void }) {
  const [shown, setShown] = useState(false);
  const recordings = useQuery({
    queryKey: ['recordings'],
    queryFn: api.recordings,
    enabled: shown,
    refetchInterval: (query) =>
      query.state.data?.recordings.some(
        (r) => r.job?.status === 'queued' || r.job?.status === 'running',
      )
        ? 10_000
        : false,
  });
  const data = recordings.data;
  return (
    <section className="recordings">
      <div className="recordings__header">
        <button type="button" className="btn btn--small" onClick={() => setShown((v) => !v)}>
          {shown ? 'Скрыть записи Zoom' : 'Записи Zoom'}
        </button>
        {shown && data?.available && (
          <span className="muted small">
            Облачные записи за 30 дней: {data.recordings.length}
            {!data.canWrite && ' · расшифровку заказывают те, у кого есть право записи'}
          </span>
        )}
      </div>
      {shown && recordings.isPending && <p className="muted">Загрузка записей…</p>}
      {shown && recordings.isError && <p className="text-error">{recordings.error.message}</p>}
      {shown && data && !data.available && (
        <p className="muted">
          Записи Zoom недоступны: Zoom не настроен или у приложения нет скоупов на облачные записи.
        </p>
      )}
      {shown && data?.available && data.recordings.length === 0 && (
        <p className="muted">За последние 30 дней облачных записей нет.</p>
      )}
      {shown && data?.available && data.recordings.length > 0 && (
        <ul className="card page-list">
          {data.recordings.map((r) => (
            <RecordingRow
              key={r.id}
              recording={r}
              canWrite={data.canWrite}
              engines={data.engines}
              onOpen={onOpen}
            />
          ))}
        </ul>
      )}
      {shown && data?.available && (
        <p className="muted small">
          Обновлено {formatShortDateTime(new Date(recordings.dataUpdatedAt).toISOString())}.
          Расшифровка идёт на сервере: час разговора занимает несколько минут, готовый текст
          появляется в списке расшифровок без краткого содержания.
        </p>
      )}
    </section>
  );
}
